import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FuseSplashScreenService } from "@barebone-fuse-services/splash-screen.service";
import { PlatformQueryService } from "@core/services/platform-query.service";
import { FlexSheetNavigationService, parseExcelCellAddress } from "@features/spreadsheet/services/flex-sheet-navigation.service";
import { FlexSheetService } from "@features/spreadsheet/services/flex-sheet.service";
import { FlexSheetCellMenuService } from "@features/spreadsheet/services/flex-sheet-cell-menu.service";
import { SnackBarService } from "@core/services/snack-bar.service";
import { CommandService } from "@core/services/command.service";
import { PlatformToolSpreadsheetHandler } from "@features/spreadsheet/services/platform-tool-spreadsheet-handler";
import { SpreadsheetQueryFactory } from "@features/spreadsheet/spreadsheet-query";
import { PlatformCommand } from "@shared/commands/command-base";
import { IPlatformHomeMagicBar } from "@shared/components/platform-tool/platform-tool-magic-bar/models/magic-bar-models";
import { HomeSideBarItem } from "@shared/models/platform-tool-models";
import { effectiveZoom, screenPxToLocal } from "@shared/helpers/effective-zoom";
import { of, Subject } from "rxjs";
import { finalize, take, takeUntil } from "rxjs/operators";

type OverwriteReviewAction = "keep" | "remove";

interface OverwriteReviewCell {
  id: string;
  worksheetId: string;
  worksheetName: string;
  workbookName: string;
  rowIndex: number;
  columnIndex: number;
  address: string;
  value: any;
  fieldName: string;
  action: OverwriteReviewAction;
  editing?: boolean;
  addressDraft?: string;
  addressEdited?: boolean; // address changed this session — sent to the worker to relocate on Apply
  addressError?: string; // inline validation message shown under the address editor
  validating?: boolean; // confirm-time target check in flight — editor busy, ✓ disabled
}

interface OverwriteReviewGroup {
  worksheetName: string;
  worksheetId: string;
  workbookName: string;
  expanded: boolean;
  loaded: boolean; // cells fetched
  loading: boolean; // fetch in flight
  count: number; // total overrides on this worksheet (from the list query)
  removeCount: number; // overrides marked Remove
  itemSize: number; // virtual-scroll row height (taller when a Field line is shown)
  cells: OverwriteReviewCell[];
}

// Post-import "Overwrite Review", surfaced in the right (home) sidebar via the magic-bar. To scale to
// a large number of overrides, it loads only a lightweight worksheet list up front and fetches each
// worksheet's cells lazily on expand. Each Keep/Remove decision is persisted immediately (resumable),
// so Apply derives the commit from the saved decisions rather than from in-memory state.
@Component({
  standalone: false,
  selector: "app-overwrite-review-sidebar",
  templateUrl: "./overwrite-review-sidebar.component.html",
  styleUrls: ["./overwrite-review-sidebar.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class OverwriteReviewSidebarComponent implements IPlatformHomeMagicBar, OnInit, OnDestroy {
  // Set by the magic-bar host from the backend HomeSideBarItem (UseHomeSideBar().Add).
  homeSideBarItem: HomeSideBarItem;
  data: any;

  groups: OverwriteReviewGroup[] = [];
  isLoading = false;
  isApplying = false;

  // Panel chrome (redesign): collapse to header only, and a left-edge drag handle that resizes the
  // panel between 400 and 700px. Purely presentational — no effect on the review data or commands.
  collapsed = false;
  panelWidth = 400;
  private readonly minWidth = 400;
  private readonly maxWidth = 700;
  private resizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  /** The handle's net CSS zoom, read once per drag — clientX is screen px, panelWidth is local px. */
  private resizeZoom = 1;

  // "View full value": a long override value is truncated in the narrow sidebar, so an eye icon opens a
  // dialog showing the full text. The icon appears only when the value exceeds this length.
  @ViewChild("valueDialog") private valueDialogTpl!: TemplateRef<unknown>;
  private readonly valueViewThreshold = 15;
  viewingValue: { worksheet: string; address: string; value: string } | null = null;
  private valueDialogRef?: MatDialogRef<unknown>;

  private unsubscribe$ = new Subject<void>();
  // In-session address edits, keyed by override id. Cells aren't cached (re-queried on each open),
  // so this small map is what survives a worksheet switch and is replayed onto freshly loaded rows.
  private addressEdits: { [id: string]: { rowIndex: number; columnIndex: number } } = {};

  constructor(
    private platformQueryService: PlatformQueryService,
    private platformToolSpreadsheetHandler: PlatformToolSpreadsheetHandler,
    private flexSheetNavigationService: FlexSheetNavigationService,
    private flexSheetService: FlexSheetService,
    private flexSheetCellMenuService: FlexSheetCellMenuService,
    private snackBarService: SnackBarService,
    private commandService: CommandService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.fetchWorksheets();
  }

  private get workbookSetId(): string {
    return this.homeSideBarItem && this.homeSideBarItem.fieldValues
      ? this.homeSideBarItem.fieldValues["WorkbookSetId"]
      : null;
  }

  // Counts come from the worksheet list (kept live for loaded sheets) so they're correct even when
  // most worksheets haven't been fetched yet.
  get totalCount(): number {
    return this.groups.reduce((n, g) => n + g.count, 0);
  }

  get removeCount(): number {
    return this.groups.reduce((n, g) => n + g.removeCount, 0);
  }

  get keepCount(): number {
    return this.totalCount - this.removeCount;
  }

  // Worksheets that actually carry overrides — drives the "across N worksheets" summary hint.
  get worksheetsWithOverrides(): number {
    return this.groups.filter((g) => g.count > 0).length;
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  // ── Left-edge resize (400–700px) ───────────────────────────────────────────
  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.panelWidth;
    this.resizeZoom = effectiveZoom(event.currentTarget as Element | null);
    document.addEventListener("mousemove", this.onResizeMove);
    document.addEventListener("mouseup", this.stopResize);
  }

  // Handle sits on the LEFT edge of a right-docked panel, so dragging left widens it.
  private onResizeMove = (event: MouseEvent): void => {
    if (!this.resizing) return;
    const next = this.resizeStartWidth + screenPxToLocal(this.resizeStartX - event.clientX, this.resizeZoom);
    this.panelWidth = Math.max(this.minWidth, Math.min(this.maxWidth, next));
  };

  private stopResize = (): void => {
    this.resizing = false;
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.stopResize);
  };

  // ── View full value ─────────────────────────────────────────────────────────
  isLongValue(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).length > this.valueViewThreshold;
  }

  openValue(cell: OverwriteReviewCell): void {
    this.viewingValue = {
      worksheet: cell.worksheetName,
      address: cell.address,
      value: cell.value === null || cell.value === undefined ? "" : String(cell.value),
    };
    this.valueDialogRef = this.dialog.open(this.valueDialogTpl, {
      panelClass: "ow-value-dialog-panel",
      width: "560px",
      maxWidth: "92vw", // density-viewport-allow: MatDialog size, converted on open by ViewportScaleService
      autoFocus: false,
    });
    this.valueDialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(() => (this.viewingValue = null));
  }

  closeValueDialog(): void {
    this.valueDialogRef?.close();
  }

  private allCells(): OverwriteReviewCell[] {
    // Only loaded worksheets have cells; the rest are empty until expanded.
    return this.groups.reduce((acc, g) => acc.concat(g.cells), [] as OverwriteReviewCell[]);
  }

  // Tolerate either Pascal- or camel-cased keys from the platform response serializer.
  private pick(source: any, ...keys: string[]): any {
    if (!source) return undefined;
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        return source[key];
      }
    }
    return undefined;
  }

  // Lightweight worksheet list; cells are fetched lazily on expand.
  fetchWorksheets(): void {
    const workbookSetId = this.workbookSetId;
    if (!workbookSetId) {
      return;
    }
    this.isLoading = true;
    const query = SpreadsheetQueryFactory.fetchOverwriteWorksheetsQuery(workbookSetId);
    this.platformQueryService
      .executeQuery(query)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          const list = this.pick(res.fieldValues, "Worksheets", "worksheets") || [];
          this.groups = list.map(
            (g: any, i: number): OverwriteReviewGroup => ({
              worksheetName: this.pick(g, "WorksheetName", "worksheetName"),
              worksheetId: this.pick(g, "WorksheetId", "worksheetId"),
              workbookName: this.pick(g, "WorkbookName", "workbookName"),
              expanded: i === 0, // first worksheet open by default
              loaded: false,
              loading: false,
              count: this.pick(g, "Count", "count") || 0,
              removeCount: this.pick(g, "RemoveCount", "removeCount") || 0,
              itemSize: 66,
              cells: [],
            })
          );
          this.isLoading = false;
          if (this.groups[0]) {
            this.loadWorksheetCells(this.groups[0]);
          }
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  // Always re-queries (cells are never cached); the in-flight guard just avoids a double fetch.
  private loadWorksheetCells(group: OverwriteReviewGroup): void {
    const workbookSetId = this.workbookSetId;
    if (!workbookSetId || group.loading) {
      return;
    }
    group.loading = true;
    const query = SpreadsheetQueryFactory.fetchOverwriteWorksheetCellsQuery(workbookSetId, group.worksheetId);
    this.platformQueryService
      .executeQuery(query)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          const list = this.pick(res.fieldValues, "Cells", "cells") || [];
          group.cells = list.map(
            (c: any): OverwriteReviewCell => ({
              id: this.pick(c, "Id", "id"),
              worksheetId: this.pick(c, "WorksheetId", "worksheetId"),
              worksheetName: this.pick(c, "WorksheetName", "worksheetName"),
              workbookName: this.pick(c, "WorkbookName", "workbookName"),
              rowIndex: this.pick(c, "RowIndex", "rowIndex"),
              columnIndex: this.pick(c, "ColumnIndex", "columnIndex"),
              address: this.pick(c, "Address", "address"),
              value: this.pick(c, "Value", "value"),
              fieldName: this.pick(c, "FieldName", "fieldName"),
              // Restore the persisted decision so the review resumes where the user left off.
              action: this.pick(c, "ReviewRemove", "reviewRemove") ? "remove" : "keep",
            })
          );
          // Restore any relocated address. A fresher in-session edit wins; otherwise the persisted
          // mid-review relocation (ReviewRowIndex/ReviewColumnIndex from the model) rebuilds the edit so
          // it survives a full reload. Rebuilding addressEdits here also keeps Apply's payload correct.
          group.cells.forEach((c, i) => {
            const sessionEdit = this.addressEdits[c.id];
            const reviewRow = this.pick(list[i], "ReviewRowIndex", "reviewRowIndex");
            const reviewCol = this.pick(list[i], "ReviewColumnIndex", "reviewColumnIndex");
            if (sessionEdit) {
              c.rowIndex = sessionEdit.rowIndex;
              c.columnIndex = sessionEdit.columnIndex;
              c.address = this.toAddress(sessionEdit.rowIndex, sessionEdit.columnIndex);
              c.addressEdited = true;
            } else if (reviewRow !== undefined && reviewCol !== undefined) {
              c.rowIndex = reviewRow;
              c.columnIndex = reviewCol;
              c.address = this.toAddress(reviewRow, reviewCol);
              c.addressEdited = true;
              this.addressEdits[c.id] = { rowIndex: reviewRow, columnIndex: reviewCol };
            }
          });
          group.itemSize = group.cells.some((c) => c.fieldName) ? 88 : 66;
          group.count = group.cells.length;
          group.removeCount = group.cells.filter((c) => c.action === "remove").length;
          group.loaded = true;
          group.loading = false;
        },
        () => {
          group.loading = false;
        }
      );
  }

  navigateToCell(cell: OverwriteReviewCell): void {
    if (!cell) {
      return;
    }
    this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
      cell.worksheetId,
      cell.rowIndex,
      cell.columnIndex,
      cell.address
    );
  }

  setAction(group: OverwriteReviewGroup, cell: OverwriteReviewCell, action: OverwriteReviewAction): void {
    cell.action = action;
    group.removeCount = group.cells.filter((c) => c.action === "remove").length;
    this.saveDecisions([cell]);
  }

  // Bulk actions — flip every override across all worksheets (loaded or not) on the server.
  keepAll(): void {
    this.setAll(false);
  }

  rejectAll(): void {
    this.setAll(true);
  }

  private setAll(remove: boolean): void {
    const workbookSetId = this.workbookSetId;
    if (!workbookSetId) {
      return;
    }
    // Optimistically reflect across all worksheets (counts + any loaded cells).
    this.groups.forEach((g) => {
      g.removeCount = remove ? g.count : 0;
      g.cells.forEach((c) => (c.action = remove ? "remove" : "keep"));
    });
    const command = new PlatformCommand("SetOverwriteReviewDecisionCommand", "SpreadsheetApi");
    command.projectId = "[PROJECT_ID]";
    command.fieldValues["WorkbookSetId"] = workbookSetId;
    command.fieldValues["SetAllRemove"] = remove;
    this.commandService.executePlatformCommand(command).pipe(take(1), takeUntil(this.unsubscribe$)).subscribe();
  }

  // Exclusive accordion. Cells are never cached: collapse everything and drop their rows, then
  // re-query the opened worksheet fresh from the model.
  toggleGroup(group: OverwriteReviewGroup): void {
    const willExpand = !group.expanded;
    this.groups.forEach((g) => {
      g.expanded = false;
      g.cells = [];
      g.loaded = false;
    });
    if (willExpand) {
      group.expanded = true;
      this.loadWorksheetCells(group);
    }
  }

  // Virtual-scroll viewport height: fit the rows, capped so a large sheet scrolls instead of growing.
  getViewportHeight(group: OverwriteReviewGroup): number {
    const rows = group.cells.length || 1;
    return Math.min(rows * group.itemSize, group.itemSize * 7);
  }

  // ── Address editing ────────────────────────────────────────────────────────
  startEdit(cell: OverwriteReviewCell): void {
    cell.addressDraft = cell.address;
    cell.addressError = undefined;
    cell.editing = true;
  }

  cancelEdit(cell: OverwriteReviewCell): void {
    if (cell.validating) {
      return;
    }
    cell.editing = false;
  }

  // Fill the draft from the cell currently selected in the live worksheet. The override only moves
  // within its own worksheet, so require the user to be on that sheet (selection.position is the
  // A1 address shown in the name box).
  useSelectedCell(cell: OverwriteReviewCell): void {
    const currentWorksheet = this.flexSheetService.getCurrentWorkSheet();
    if (!currentWorksheet || currentWorksheet.id !== cell.worksheetId) {
      this.snackBarService.showWarning(`Select a cell on worksheet "${cell.worksheetName}" first.`);
      return;
    }
    const position = this.flexSheetService.selection?.position;
    if (!position) {
      this.snackBarService.showWarning("Select a cell in the worksheet first.");
      return;
    }
    // Fast-path: the clicked cell sits on a loaded page, so reject a non-overwrite cell right here with
    // no backend call. The selection's cell model is looked up by the grid's visual row, and
    // isOverwriteCell reads its server-stamped `ow` flag / Excel `er`+`ec` against the table-infos.
    // No cell model → fail open: fill the draft and let the confirm-time backend check decide.
    const cellModel = this.flexSheetService.selection?.cellModel;
    if (cellModel && typeof cellModel.ec === "number" && !this.flexSheetCellMenuService.isOverwriteCell(cellModel)) {
      this.snackBarService.showWarning(`Selected cell ${position} is not an overwritable cell.`);
      return;
    }
    cell.addressDraft = position;
    cell.addressError = undefined;
    cell.editing = true;
  }

  confirmEdit(cell: OverwriteReviewCell): void {
    if (cell.validating) {
      return; // a check is already in flight — ignore the double-confirm
    }
    const parsed = parseExcelCellAddress((cell.addressDraft || "").trim());
    if (!parsed) {
      // Inline error (matches the design) instead of a transient snackbar; keep the editor open.
      cell.addressError = "Enter a valid cell reference, e.g. B12";
      return;
    }
    // Validate the target against the live workbook before saving anything. Queries can't reach the
    // workbook, so this goes through the event-source wrapper to the job, like the overwrite toggle.
    const workbookSetId = this.workbookSetId;
    if (!workbookSetId) {
      return;
    }
    const command = new PlatformCommand("AddEventSourceModelCommand", "SpreadsheetApi");
    command.projectId = "[PROJECT_ID]";
    command.fieldValues["CommandName"] = "ValidateOverwriteTargetCommand";
    command.fieldValues["EventSourceId"] = workbookSetId;
    command.fieldValues["SourceType"] = "WorkbookSet";
    command.fieldValues["WorkbookSetId"] = workbookSetId;
    command.fieldValues["WorksheetId"] = cell.worksheetId;
    command.fieldValues["RowIndex"] = parsed.row;
    command.fieldValues["ColumnIndex"] = parsed.col;
    command.fieldValues["OverwriteCellId"] = cell.id;

    cell.validating = true;
    cell.addressError = undefined;
    let answered = false;
    this.commandService
      .executePlatformCommand(command)
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          cell.validating = false;
          if (!answered) {
            cell.addressError = "Could not validate the address — try again.";
          }
        })
      )
      .subscribe(
        (payload) => {
          const valid = payload?.fieldValues?.["Valid"];
          if (valid === true) {
            answered = true;
            this.acceptAddress(cell, parsed.row, parsed.col);
          } else if (valid === false) {
            answered = true;
            cell.addressError = payload.fieldValues["Reason"] || `${this.toAddress(parsed.row, parsed.col)} is not an overwritable cell`;
          }
        },
        () => {
          /* transport failure — finalize shows the retryable error */
        }
      );
  }

  // The target passed validation: apply it locally and persist it (unchanged from the pre-validation flow).
  private acceptAddress(cell: OverwriteReviewCell, row: number, col: number): void {
    cell.rowIndex = row;
    cell.columnIndex = col;
    cell.address = this.toAddress(row, col);
    cell.addressEdited = true;
    cell.addressError = undefined;
    cell.editing = false;
    // Remember the edit so it survives a worksheet switch (cells are re-queried, not cached)…
    this.addressEdits[cell.id] = { rowIndex: row, columnIndex: col };
    // …and persist it immediately (like a Keep/Remove toggle) so it also survives a full reload.
    this.saveDecisions([cell]);
  }

  // 0-based row/col -> A1 address (mirrors the backend GetCellAddress).
  private toAddress(rowIndex: number, columnIndex: number): string {
    let dividend = columnIndex + 1;
    let columnName = "";
    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }
    return `${columnName}${rowIndex + 1}`;
  }

  // Persist each Keep/Remove decision so the (forced, interruptible) review can be resumed.
  private saveDecisions(cells: OverwriteReviewCell[]): void {
    const workbookSetId = this.workbookSetId;
    if (!workbookSetId || !cells.length) {
      return;
    }
    const command = this.buildDecisionsCommand(
      cells.map((c) => {
        const decision: { Id: string; Remove: boolean; RowIndex?: number; ColumnIndex?: number } = {
          Id: c.id,
          Remove: c.action === "remove",
        };
        // Carry the relocated position so it persists immediately (and survives a reload); absent for a
        // plain Keep/Remove toggle, which leaves any existing relocation untouched on the server.
        const edit = this.addressEdits[c.id];
        if (edit) {
          decision.RowIndex = edit.rowIndex;
          decision.ColumnIndex = edit.columnIndex;
        }
        return decision;
      })
    );
    this.commandService.executePlatformCommand(command).pipe(take(1), takeUntil(this.unsubscribe$)).subscribe();
  }

  private buildDecisionsCommand(
    decisions: Array<{ Id: string; Remove: boolean; RowIndex?: number; ColumnIndex?: number }>
  ): PlatformCommand {
    const command = new PlatformCommand("SetOverwriteReviewDecisionCommand", "SpreadsheetApi");
    command.projectId = "[PROJECT_ID]";
    command.fieldValues["WorkbookSetId"] = this.workbookSetId;
    command.fieldValues["Decisions"] = decisions;
    return command;
  }

  apply(): void {
    const workbookSetId = this.workbookSetId;
    if (this.isApplying || !workbookSetId) {
      return;
    }
    this.isApplying = true;
    this.fuseSplashScreenService.show();

    const loaded = this.allCells();
    // Address edits come from the session map (cells aren't cached). The worker only honors edits for
    // kept overrides — a removed one is deleted regardless — so sending them all is safe.
    const addressEdits = Object.keys(this.addressEdits).map((id) => ({
      Id: id,
      RowIndex: this.addressEdits[id].rowIndex,
      ColumnIndex: this.addressEdits[id].columnIndex,
    }));

    // Flush the open worksheet's decisions first so a just-made toggle is persisted before the worker
    // reads them, then commit. (Other worksheets' decisions were already persisted on toggle.)
    const flush$ = loaded.length
      ? this.commandService.executePlatformCommand(
          this.buildDecisionsCommand(loaded.map((c) => ({ Id: c.id, Remove: c.action === "remove" })))
        )
      : of(null);

    flush$.pipe(take(1), takeUntil(this.unsubscribe$)).subscribe(() => this.dispatchApply(addressEdits));
  }

  private dispatchApply(addressEdits: Array<{ Id: string; RowIndex: number; ColumnIndex: number }>): void {
    // The opener decides which entry commits the review and what context the reopen needs, via
    // FieldValues: FilingManager forms (ApplyOverwriteReviewCommand → reopen the form) and GMT GIR
    // reports (ApplyGirOverwriteReviewCommand → reopen the report) both flow through here. Default to
    // the FilingManager entry for backward compatibility.
    const fieldValues = this.homeSideBarItem?.fieldValues || {};
    const applyCommandName = fieldValues["ApplyCommandName"] || "ApplyOverwriteReviewCommand";
    const applyApi = fieldValues["ApplyApi"] || "FilingManagerApi";

    const command = new PlatformCommand(applyCommandName, applyApi);
    command.projectId = "[PROJECT_ID]";
    // Forward whatever reopen context the opener provided (FM: ResponseId/RelativePath; GMT:
    // ScenarioId/ScenarioType/CountryCode/SelectedReportId/FileId/DriveId/ReportFileName).
    Object.keys(fieldValues).forEach((key) => (command.fieldValues[key] = fieldValues[key]));
    command.fieldValues["WorkbookSetId"] = this.workbookSetId;
    command.fieldValues["AddressEdits"] = addressEdits;

    // Dispatch here rather than through the spreadsheet handler: its response path ignores
    // SnackBarErrorMessage and would leave this sidebar stuck in Applying. A rejected batch (e.g. an
    // invalid relocation target, SA-E-151-02) comes back as SnackBarErrorMessage with nothing applied —
    // show it and reset, leaving the review open so the offending address can be fixed inline. Success
    // hands the reopen RedirectCommand back to the handler exactly as before.
    let answered = false;
    this.commandService
      .executePlatformCommand(command)
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          if (!answered) {
            this.resetApplying();
          }
        })
      )
      .subscribe(
        (response) => {
          answered = true;
          const fieldValues = response?.fieldValues || {};
          if (fieldValues["SnackBarErrorMessage"] || !fieldValues["RedirectCommand"]) {
            this.snackBarService.showError(fieldValues["SnackBarErrorMessage"] || "Overwrite review could not be applied — try again.");
            this.resetApplying();
            return;
          }
          this.fuseSplashScreenService.hide();
          this.platformToolSpreadsheetHandler.executeCommand(fieldValues["RedirectCommand"]);
        },
        () => {
          answered = true;
          this.snackBarService.showError("Overwrite review could not be applied — try again.");
          this.resetApplying();
        }
      );
  }

  private resetApplying(): void {
    this.isApplying = false;
    this.fuseSplashScreenService.hide();
  }

  ngOnDestroy(): void {
    this.stopResize();
    this.valueDialogRef?.close();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
